import type { api } from "@breakout/backend/convex/_generated/api"
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuRadioGroup,
	DropdownMenuRadioItem,
	DropdownMenuTrigger,
} from "@breakout/ui/components/DropdownMenu"
import { cn, formatShares } from "@breakout/ui/utils"
import { Link } from "@tanstack/react-router"
import type { FunctionReturnType } from "convex/server"
import { ChevronDown } from "lucide-react"
import { motion } from "motion/react"
import { memo, useMemo, useState } from "react"
import type { Address } from "viem"
import { useTopHolders } from "../../hooks/indexer/useTopHolders"
import type { OutcomeBucket } from "../../lib/types"
import { ErrorState } from "../ui/ErrorState"
import { SimpleAvatar } from "../ui/SimpleAvatar"
import { Skeleton } from "../ui/Skeleton"
import { GridSkeleton, SingleColumnSkeleton } from "../ui/TopHoldersSkeleton"

type TopHolder = FunctionReturnType<typeof api.indexer.public.getTopHolders>[number]

interface TopHoldersProps {
	marketAddress: Address | undefined
	/** Outcome buckets with labels and directions - passed from parent to avoid redundant fetching */
	buckets: OutcomeBucket[]
	/** For Fall from Grace markets, always show dropdown selector (even on desktop) */
	isFallFromGrace?: boolean
}

/**
 * Top Holders component
 *
 * Displays top holders for each outcome bucket in a market.
 * - Mobile (all markets): dropdown selector with single column view
 * - Desktop binary: two columns (Long/Short or custom outcome labels)
 * - Desktop Fall from Grace: dropdown selector with single column view
 */
export const TopHolders = memo(function TopHolders({
	marketAddress,
	buckets,
	isFallFromGrace = false,
}: TopHoldersProps) {
	const [selectedBucketId, setSelectedBucketId] = useState<string>("0")

	const {
		data: holders,
		isLoading,
		error,
	} = useTopHolders(marketAddress ? { marketAddress } : null)

	// Group holders by bucket (backend already sorts by shares descending and limits to top N)
	const holdersByBucket = useMemo(() => {
		if (!holders) {
			return new Map<number, TopHolder[]>()
		}

		const grouped = new Map<number, TopHolder[]>()
		for (const holder of holders) {
			const existing = grouped.get(holder.bucket) ?? []
			existing.push(holder)
			grouped.set(holder.bucket, existing)
		}

		return grouped
	}, [holders])

	const selectedBucket = buckets.find(b => b.id === selectedBucketId) ?? buckets[0]
	const isBinary = buckets.length === 2

	// Loading state
	if (isLoading) {
		return (
			<div className="w-full overflow-hidden">
				{/* Mobile skeleton: dropdown + single column (always) */}
				{/* Desktop skeleton: grid for binary, dropdown for FFG */}
				<div className={isFallFromGrace ? "w-full" : "sm:hidden"}>
					<div className="mb-4">
						<Skeleton className="h-10 w-48 rounded-xl" />
					</div>
					<SingleColumnSkeleton />
				</div>
				{!isFallFromGrace && (
					<div className="hidden sm:block">
						<GridSkeleton />
					</div>
				)}
			</div>
		)
	}

	if (error) {
		return <ErrorState title="Failed to load holders" variant="inline" />
	}

	const currentHolders = selectedBucket
		? (holdersByBucket.get(Number(selectedBucket.id)) ?? [])
		: []

	return (
		<div className="w-full overflow-hidden">
			{/* Mobile: dropdown selector + single column list */}
			{/* Desktop FFG: same as mobile */}
			<div className={isFallFromGrace ? "w-full" : "sm:hidden"}>
				<DropdownSelector
					buckets={buckets}
					onChange={setSelectedBucketId}
					value={selectedBucketId}
				/>
				<HoldersList
					holders={currentHolders}
					isPositive={selectedBucket?.direction === "up"}
					key={selectedBucketId}
					label={selectedBucket?.label ?? ""}
				/>
			</div>

			{/* Desktop grid for binary markets only */}
			{!isFallFromGrace && isBinary && (
				<div className="hidden sm:block">
					<div className="grid gap-6 sm:grid-cols-2">
						{buckets.map(bucket => {
							const bucketHolders = holdersByBucket.get(Number(bucket.id)) ?? []
							return (
								<HoldersList
									holders={bucketHolders}
									isPositive={bucket.direction === "up"}
									key={bucket.id}
									label={bucket.label}
								/>
							)
						})}
					</div>
				</div>
			)}
		</div>
	)
})

// =============================================================================
// Subcomponents
// =============================================================================

interface DropdownSelectorProps {
	buckets: { id: string; label: string }[]
	value: string
	onChange: (value: string) => void
}

const DropdownSelector = memo(function DropdownSelector({
	buckets,
	value,
	onChange,
}: DropdownSelectorProps) {
	const selected = buckets.find(b => b.id === value) ?? buckets[0]

	return (
		<div className="mb-4">
			<DropdownMenu>
				<DropdownMenuTrigger asChild>
					<button
						aria-label="Select outcome to view holders"
						className="inline-flex items-center gap-2 rounded-xl border border-border-subtle bg-bg-card px-4 py-2.5 font-medium text-text-primary transition-colors hover:bg-bg-secondary"
						type="button"
					>
						{selected?.label ?? "Select outcome"}
						<ChevronDown aria-hidden="true" className="size-4 text-text-muted" />
					</button>
				</DropdownMenuTrigger>
				<DropdownMenuContent align="start" className="min-w-[200px]">
					<DropdownMenuRadioGroup onValueChange={onChange} value={value}>
						{buckets.map(bucket => (
							<DropdownMenuRadioItem key={bucket.id} value={bucket.id}>
								{bucket.label}
							</DropdownMenuRadioItem>
						))}
					</DropdownMenuRadioGroup>
				</DropdownMenuContent>
			</DropdownMenu>
		</div>
	)
})

interface HoldersListProps {
	holders: TopHolder[]
	isPositive: boolean
	label: string
}

const HoldersList = memo(function HoldersList({ holders, isPositive, label }: HoldersListProps) {
	return (
		<motion.div
			animate="show"
			initial="hidden"
			variants={{
				hidden: { opacity: 0 },
				show: {
					opacity: 1,
					transition: { staggerChildren: 0.05 },
				},
			}}
		>
			<div className="mb-2 flex items-center px-2">
				<h3 className="min-w-0 flex-1 truncate font-semibold text-sm text-text-primary">
					{label} holders
				</h3>
				<span className="shrink-0 text-text-muted text-xs uppercase">Shares</span>
			</div>

			{holders.length === 0 ? (
				<p className="px-2 py-4 text-sm text-text-muted">No holders yet</p>
			) : (
				<div className="space-y-0.5">
					{holders.map(holder => (
						<HolderRow
							holder={holder}
							isPositive={isPositive}
							key={`${holder.trader}-${holder.bucket}`}
						/>
					))}
				</div>
			)}
		</motion.div>
	)
})

interface HolderRowProps {
	holder: TopHolder
	isPositive: boolean
}

const HolderRow = memo(function HolderRow({ holder, isPositive }: HolderRowProps) {
	// avatarUrl is always provided by the backend (with DiceBear fallback applied server-side)
	const profileIdentifier = holder.username ?? holder.trader

	return (
		<motion.div
			className="flex items-center gap-2 rounded-lg px-2 py-1.5 transition-colors hover:bg-bg-secondary/50"
			variants={{
				hidden: { opacity: 0, x: -10 },
				show: { opacity: 1, x: 0 },
			}}
		>
			<Link params={{ identifier: profileIdentifier }} to="/u/$identifier">
				<SimpleAvatar
					alt={`${holder.displayName}'s avatar`}
					className="size-7"
					hoverEffect
					size="sm"
					src={holder.avatarUrl}
					variant="circle"
				/>
			</Link>
			<Link
				className="min-w-0 flex-1 truncate text-sm text-text-primary hover:text-text-secondary"
				params={{ identifier: profileIdentifier }}
				to="/u/$identifier"
			>
				@{holder.displayName}
			</Link>
			<span
				className={cn(
					"shrink-0 text-sm tabular-nums",
					isPositive ? "text-text-positive" : "text-text-negative",
				)}
			>
				{formatShares(holder.shares)}
			</span>
		</motion.div>
	)
})

// Skeletons moved to ../ui/TopHoldersSkeleton
